import React, { createContext, useContext, useState, useCallback } from 'react';

const WorkflowPromptContext = createContext();

export const useWorkflowPrompt = () => {
  const context = useContext(WorkflowPromptContext);
  if (!context) {
    throw new Error('useWorkflowPrompt must be used within WorkflowPromptProvider');
  }
  return context;
};

export const WorkflowPromptProvider = ({ children }) => {
  const [pendingPrompts, setPendingPrompts] = useState([]);
  const [currentPrompt, setCurrentPrompt] = useState(null);

  const addPendingPrompt = useCallback((prompt) => {
    setPendingPrompts(prev => {
      const updated = [...prev, prompt];
      // If no current prompt is showing, show the first one
      if (!currentPrompt && updated.length > 0) {
        setCurrentPrompt(updated[0]);
      }
      return updated;
    });
  }, [currentPrompt]);

  const removePendingPrompt = useCallback((executionId) => {
    setPendingPrompts(prev => {
      const filtered = prev.filter(p => p.executionId !== executionId);
      // If we removed the current prompt, show the next one
      if (currentPrompt && currentPrompt.executionId === executionId) {
        setCurrentPrompt(filtered.length > 0 ? filtered[0] : null);
      }
      return filtered;
    });
  }, [currentPrompt]);

  const showNextPrompt = useCallback(() => {
    setPendingPrompts(prev => {
      if (prev.length > 0) {
        setCurrentPrompt(prev[0]);
        return prev;
      } else {
        setCurrentPrompt(null);
        return [];
      }
    });
  }, []);

  /** Remove the given execution from the queue, optionally append nextPrompt, then show the first in queue. */
  const completeCurrentAndEnqueueNext = useCallback((executionId, nextPrompt) => {
    setPendingPrompts(prev => {
      const filtered = prev.filter(p => p.executionId !== executionId);
      const next = nextPrompt ? [...filtered, nextPrompt] : filtered;
      setCurrentPrompt(next.length > 0 ? next[0] : null);
      return next;
    });
  }, []);

  const clearCurrentPrompt = useCallback(() => {
    setCurrentPrompt(null);
  }, []);

  const value = {
    pendingPrompts,
    currentPrompt,
    addPendingPrompt,
    removePendingPrompt,
    showNextPrompt,
    completeCurrentAndEnqueueNext,
    clearCurrentPrompt
  };

  return (
    <WorkflowPromptContext.Provider value={value}>
      {children}
    </WorkflowPromptContext.Provider>
  );
};
